/**
 * bundle.schema.ts
 * ----------------
 * Zod schema for BundleBuilderConfigInterface.
 *
 * This single schema is used for BOTH the base document and every variant
 * document — variants are identical shapes, just with different values.
 * Mirrors VolumeDiscount/hooks/bundle.schema.ts: real validation on the
 * fields that matter (required text, cross-field business rules — reusing
 * the existing validator functions from `apps/Bundles/constants` inside
 * `superRefine` so the rules stay identical to the pre-migration form), and
 * permissive typing (`z.any()`) on the large "opaque payload" arrays
 * (products/collections/images/etc.) that aren't hand-validated today.
 */

import { z } from "zod";
import {
    arrayOf,
    boolean,
    coercedNumber,
    DiscountTypeSchema,
    fontSize,
    hexColor,
    ImageSchema,
    loose,
    looseArray,
    looseObject,
    nullableBoolean,
    nullableOptionalNumber,
    nullableOptionalString,
    number,
    optionalArrayOf,
    optionalBoolean,
    optionalEnum,
    optionalNumber,
    optionalString,
    requiredBoolean,
    requiredEnum,
    requiredNumber,
    requiredString,
    string,
} from "../../hooks/schemaHelpers";

// ---------------------------------------------------------------------------
// Primitives / Enums
// ---------------------------------------------------------------------------

export const VariantSchema = z.object({
    id: string(),
    displayName: string(),
    title: string(),
    price: string(),
    image: loose(),
    quantity: optionalNumber(),
    selectedOptions: looseArray(),
    selling_plan: optionalString(),
    copyPrice: optionalString(),
    quantityAvailable: optionalNumber(),
    availableForSale: optionalBoolean(),
    compareAtPrice: string(),
    weight: nullableOptionalNumber(),
    weight_unit: nullableOptionalString(),
    qty: optionalNumber(),
    dosage_daily_dose: optionalString(),
    dosage_no_of_days: optionalString(),
    dosage_total_needed: optionalString(),
});

export const SubscriptionSchema = z.object({
    id: string(),
    name: string(),
    checked: boolean(),
    discountType: DiscountTypeSchema,
    discountValue: number(),
});

export const ProductSchema = z.object({
    id: string(),
    images: arrayOf(ImageSchema),
    title: string(),
    handle: string(),
    quantity: number(),
    variants: arrayOf(VariantSchema),
    maxProductStock: number(),
    selectedVariants: arrayOf(VariantSchema),
    metafield: z
        .object({
            value: string(),
            type: z.literal("json"),
        })
        .nullable()
        .optional(),
    options: looseArray(),
    checked: optionalBoolean(),
    subscriptions: arrayOf(SubscriptionSchema),
    oneTimePurchase: boolean(),
    isSubscribed: boolean(),
    discountType: loose(),
    discountValue: loose(),
    page: optionalString(),
    subStepIdx: optionalNumber(),
    parentName: optionalString(),
    tags: optionalArrayOf(z.string()),
    isAddon: optionalBoolean(),
    parentProduct: optionalString(),
    subStep: optionalString(),
    isPreselected: optionalBoolean(),
    freeGift: optionalBoolean(),
    custom_text: optionalString(),
    exclude_from_step_conditions: optionalBoolean(),
});

export const CollectionSchema = z.object({
    id: string(),
    image: z
        .object({
            originalSrc: optionalString(),
        })
        .optional(),
    title: string(),
});

// export type Variant = z.infer<typeof VariantSchema>;
// export type Product = z.infer<typeof ProductSchema>;
// export type Subscription = z.infer<typeof SubscriptionSchema>;

// const WidgetShowOptionsSchema = z.enum(["show_everywhere", "show_only_when_targeting", "hide_everywhere"]);

// ---------------------------------------------------------------------------
// Bundle-as-product (product page override)
// ---------------------------------------------------------------------------
const BundleAsProductSchema = z.object({
    product_title: string(""),
    product_description: string(""),
    show_on_product_page: arrayOf(z.string(), []),
    images: arrayOf(
        z.object({
            id: optionalString(),
            originalSource: string(),
            alt: optionalString(),
            mediaContentType: z.literal("IMAGE").optional(),
        }),
        [],
    ),
    product_handle: string(""),
    variant_id: optionalString(),
    double_discount: optionalBoolean(),
    collections: z.any().default([]),
    // collections: z.array(z.any()),
    //  collections: z.array(
    //     z.object({
    //         id: z.string(),
    //         image: z
    //             .object({
    //                 originalSrc: z.string().optional(),
    //             })
    //             .optional(),
    //         title: z.string(),
    //     }),
    // ),
});

/**
 * ---------------------------------------------------------------------------
 * Schema — fields kept in the EXACT same order as BundlesDesignSchema
 * ---------------------------------------------------------------------------
 */
const BundlesDesignSchema = z.object({
    default_cta: optionalBoolean(),
    bundle_title_size: fontSize("Bundle title font size"),
    bundle_title_color: hexColor("Bundle title"),
    bundle_title_style: optionalString(),
    bundle_widget_bg: hexColor("Bundle widget"),
    bundle_widget_font_family: optionalString(),
    product_name_size: fontSize("Product name font"),
    product_name_color: hexColor("Product name"),
    product_name_style: optionalString(),
    product_price_size: fontSize("Product price font"),
    product_price_color: hexColor("Product price"),
    product_price_style: optionalString(),
    product_img_size: optionalString(),
    is_product_img_height_auto: optionalBoolean(),
    product_custom_img_size: optionalNumber(160),
    product_custom_img_size_mobile: optionalNumber(160),
    product_img_show: optionalString(),
    // product_img_border_radius: optionalNumber(),
    // product_img_border_width: fontSize("Product image border width", { min: 0, max: 10 }),
    // product_img_border_color: hexColor("Product image border"),
    // border_color: hexColor("Product border"),
    border_width: fontSize("Border width font", { min: 0, max: 15 }),
    border_radius: optionalNumber(),
    // background_color: hexColor("Product background"),
    // highlighted_offer_tag_size: fontSize("Offer tag font"),
    // highlighted_offer_tag_color: hexColor("Highlighted offer tag"),
    // highlighted_offer_tag_style: optionalString(),
    highlighted_price_tag_size: fontSize("Price tag font"),
    highlighted_price_tag_color: hexColor("Highlighted price tag"),
    highlighted_price_tag_style: optionalString(),
    total_footer_text_size: fontSize("Total footer text font"),
    total_footer_text_color: hexColor("Bottom bar text"),
    total_footer_price_color: hexColor("Bottom bar price"),
    total_footer_strike_off_price_color: hexColor("Bottom bar strike off price"),
    total_footer_discount_color: hexColor("Bottom bar discount"),
    total_footer_text_style: optionalString(),
    // total_footer_price_text_size: fontSize("Total footer price font"),
    // total_footer_price_text_color: hexColor("Total footer price text"),
    // total_footer_price_text_style: optionalString(),
    total_footer_background_color: hexColor("Bottom bar background"),
    // total_footer_background_style: optionalString(),
    product_background_color: hexColor("Product background"),
    product_border_color: hexColor("Product border"),
    variant_selector_text_size: fontSize("Variant Selector size", null, 12),
    variant_selector_text_color: hexColor("Variant Selector", "#383838"),
    variant_selector_text_style: optionalString("450 normal"),
    add_to_cart_bg_color: hexColor("Add to cart background"),
    edit_bundle_cta_bg_color: hexColor("Edit bundle CTA background"),
    edit_bundle_cta_text_color: hexColor("Edit bundle text"),
    edit_bundle_cta_size: fontSize("Edit bundle CTA font size"),
    edit_bundle_cta_style: optionalString(),
    add_to_cart_border_radius: optionalNumber(),
    // add_to_cart_border_color: hexColor("Add to cart border"),
    // add_to_cart_border_width: fontSize("Add to cart border width", { min: 0, max: 10 }),
    add_to_cart_text_color: hexColor("Add to cart text"),
    add_to_cart_text_size: fontSize("Add to cart text font"),
    add_to_cart_text_style: optionalString(),
    // free_gift_header_bg_color: hexColor("Free gift header background"),
    // free_gift_header_color: hexColor("Free gift header text"),
    // free_gift_header_size: fontSize("Free gift header text font"),
    // free_gift_header_style: optionalString(),
    // free_gift_bg_color: hexColor("Free gift background"),
    // free_gift_text_color: hexColor("Free gift text"),
    // free_gift_text_size: fontSize("Free gift text font"),
    // free_gift_text_style: optionalString(),
    // free_gift_border: z.boolean().optional(),
    // free_gift_border_width: fontSize("Free gift border width", { min: 0, max: 10 }),
    // free_gift_border_color: hexColor("Free gift border"),
    fixed_boxes_bg_color: hexColor("Fixed boxes background"),
    fixed_boxes_selected_bg_color: hexColor("Fixed boxes Selected background"),
    fixed_boxes_text_color: hexColor("Fixed boxes text"),
    fixed_boxes_selected_text_color: hexColor("Fixed boxes Selected text"),
    fixed_boxes_text_size: fontSize("Fixed boxes text size"),
    secondary_cta_bg_color: hexColor("Next button background"),
    secondary_cta_text_color: hexColor("Next button text"),
    tertiary_cta_bg_color: hexColor("Previous button background"),
    tertiary_cta_text_color: hexColor("Previous button text"),
    review_product_border_color: hexColor("Review Product border"),
    review_product_border_width: fontSize("Review product border width", { min: 0, max: 10 }),
    review_product_border_radius: optionalNumber(),
    review_product_img_border_color: hexColor("Review Product border"),
    review_product_img_border_radius: optionalNumber(),
    review_product_img_border_width: fontSize("Review product image border width", { min: 0, max: 10 }),
    step_label_color: hexColor("Step label"),
    step_page_title_color: hexColor("Step page title"),
    step_page_title_size: fontSize("Step page title font size"),
    step_page_title_style: optionalString(),
    step_circle_bg_color: hexColor("Step background"),
    step_circle_color: hexColor("Step circle"),
    step_page_subtitle_color: hexColor("Step page subtitle"),
    step_page_subtitle_size: fontSize("Step page subtitle font size"),
    step_page_subtitle_style: optionalString(),
    order_summary_bg_color: hexColor("Order summary background"),
    order_summary_header_text_color: hexColor("Order summary header text"),
    order_summary_header_text_size: fontSize("Order summary header text"),
    order_summary_header_text_style: optionalString(),
    order_summary_rows_text_color: hexColor("Order summary rows text"),
    order_summary_rows_text_size: fontSize("Order summary rows text"),
    order_summary_rows_text_style: optionalString(),
    bb_margin_top: optionalNumber(0),
    bb_margin_bottom: optionalNumber(10),
    product_card_title_alignment: optionalString("left"),
    substep_bg_color: hexColor("SubStep CTA", "#303030"),
    substep_text_color: hexColor("SubStep CTA", "#ffffff"),
    substep_header_text_size: fontSize("SubStep header text size", null, 14),
    mobile_substep_header_layout: optionalString("dropdown"),
    // popup_cta_bg_color: hexColor("Popup CTA background"),
    popup_bg_color: hexColor("Popup background", "#FFFFFF"),
    popup_text_color: hexColor("Popup text", "#303030"),
    progress_bar_color: hexColor("Progress bar color"),
    progress_bar_bg_color: hexColor("Progress bar background color"),
    addon_title_text_color: hexColor("Addon title text color"),
    addon_title_text_size: fontSize("Addon title text size"),
    addon_card_bg_color: hexColor("Addon card background color"),
});

// ---------------------------------------------------------------------------
// Steps / Sub-steps / Review step / Free gift step
// ---------------------------------------------------------------------------
const StepConditionSchema = z.object({
    basis: requiredEnum(["items_count", "bundle_total"], "Condition basis is required"),
    type: requiredEnum(["max_products", "min_products", "exact_products"], "Condition type is required"),
    limit: coercedNumber(),
    msg: string(),
});

const SubStepSchema = z.object({
    step_label: string(),
    product_selection: requiredEnum(["specific", "collection"], "Product selection is required", "specific"),
    products: arrayOf(ProductSchema),
    collections: arrayOf(CollectionSchema),
    subStep_condition_basis: requiredEnum(["items_count", "bundle_total"], "Condition basis is required"),
    subStep_condition_type: requiredEnum(
        ["max_products", "min_products", "exact_products"],
        "Condition type is required",
    ),
    product_count_limit: coercedNumber(),
    step_error_msg: string("{category_label}: Select at least {productCount} products"),
    exempt_product_ids: optionalArrayOf(z.string()),
    allow_product_custom_text: optionalBoolean(),
    product_custom_text_label: optionalString(),
    product_custom_text_placeholder: optionalString(),
    hidden: optionalBoolean(),
});

const FileUploadSettingsSchema = z.object({
    max_file_size: number(),
    allow_multiple: boolean(),
    allowed_file_types: arrayOf(z.enum(["images", "documents"])),
});

const StepSchema = z.object({
    step_label: string(),
    page_title: optionalString(),
    page_subtitle: optionalString(),
    header_subtitle: optionalString(),
    bottom_subtitle: optionalString(),
    step_icon: z
        .object({
            originalSrc: string(),
            alt: optionalString(),
        })
        .nullable()
        .optional(),
    step_content_type: requiredEnum(["fields", "products", "free_gift"], "Step content type is required"),
    product_selection: requiredEnum(["specific", "collection"], "Product selection is required"),
    enable_subSteps: boolean(),
    subSteps: arrayOf(SubStepSchema),
    products: arrayOf(ProductSchema),
    collections: arrayOf(CollectionSchema),
    step_conditions: arrayOf(StepConditionSchema),
    fields: arrayOf(
        z.object({
            field_type: requiredEnum(["input", "textarea", "dropdown", "file_upload"], "Field type is required"),
            field_title: string(),
            field_placeholder: string(),
            is_required: optionalBoolean(false),
            dropdown_options: optionalArrayOf(z.string()),
            default_value: optionalString(),
            file_upload_settings: FileUploadSettingsSchema.optional(),
        }),
        [],
    ),
    render_as_variants: boolean(),
    show_step_conditions: boolean(),
    show_banner: boolean(),
    banner_desktop: string(),
    banner_mobile: string(),
    substep_rules_type: requiredEnum(["no_rules", "step_level", "substep_level"], "Substep rules type is required"),
    allow_product_custom_text: optionalBoolean(),
    product_custom_text_label: optionalString(),
    product_custom_text_placeholder: optionalString(),
    exempt_product_ids: optionalArrayOf(z.string()),
    enable_dosage_calculation: optionalBoolean(),
    enable_capacity_progress_bar: optionalBoolean(),
    merge_total_capacity: optionalBoolean(),
});

const ReviewStepSchema = z.object({
    step_label: string(),
    page_title: optionalString(),
    is_enabled: boolean(),
    step_icon: z
        .object({
            originalSrc: string(),
            alt: optionalString(),
        })
        .nullable()
        .optional(),
    enable_addon_products: optionalBoolean(),
    addon_section_title: string(),
    addon_product_discount_label: string(),
    addon_button_text: string(),
    addon_products_total_lable: string(),
    addon_product_selection: requiredEnum(["specific", "collection"], "Product selection is required"),
    addon_products: arrayOf(ProductSchema),
    collections: arrayOf(CollectionSchema),
    apply_discount_on_addon_products: optionalBoolean(),
    discount_type: DiscountTypeSchema.optional(),
    discount_value: nullableOptionalNumber(),
});
const FixedBoxSchema = z.object({
    itemsCount: number(),
    fixedPrice: number(),
    boxName: string(),
});

const BBTierOptionSchema = z.object({
    id: string(),
    discountType: DiscountTypeSchema,
    discountValue: number(),
    quantity: number(),
    maxQuantity: number(),
    addRange: nullableBoolean(),
    finalSuccessMessage: optionalString(),
    showFinalSuccessMessage: optionalBoolean(),
    TierSuccessMessage: string(),
    ShowTierSuccessMessage: boolean(true),
    selected_products: optionalArrayOf(z.any()),
});

// ---------------------------------------------------------------------------
// Targeting
// ---------------------------------------------------------------------------
const TargetingSchema = z.object({
    // includeCustomers: z.string(),
    // excludeCustomers: z.string(),
    // includeCustomerData: z.string(),
    // excludeCustomerData: z.string(),
    // shopifyMarketsTargeting: z.string(),
    // shopifyMarketsTargetingData: z.array(z.string()),
    // includeLocations: z.array(z.string()),
    // excludeLocations: z.array(z.string()),
    targetingType: string(),
    customerTagsEnabled: boolean(),
    customerTagType: string(),
    customerTags: string(),
    customerSpentEnabled: boolean(),
    customerSpentType: string(),
    customerSpent: string(),
    customerCountriesEnabled: boolean(),
    customerCountryType: string(),
    customerCountries: arrayOf(z.string()),
});

// ---------------------------------------------------------------------------
// Settings (skip_keys)
// ---------------------------------------------------------------------------
const GeneralSettingsSchema = z
    .object({
        show_bundle_on_product_page: optionalBoolean(),
        show_multiple_bundles_on_product_page: optionalBoolean(),
        redirect_after_adding_product_to_cart: optionalString(""),
        redirect_to_checkout: optionalBoolean(),
        show_powered_by_kefi: optionalBoolean(),
        max_width: optionalNumber(),
        override_max_width: optionalBoolean(),
        override_margin_top: optionalBoolean(),
        margin_top: optionalNumber(),
        bundle_alignment: optionalString(),
        show_carousel: optionalBoolean(),
        auto_rotate_carousel: optionalBoolean(),
        carousel_interval: optionalString(),
    })
    .optional();

const BundleSettingsSchema = z
    .object({
        enable_stock_control: boolean(),
        out_of_stock_text: string(),
        listing_variant_with_image: boolean(),
        work_only_with_widget: boolean(),
        bundle_position: string(),
        custom_selector: string(),
        custom_css: optionalString(),
        custom_js: optionalString(),
    })
    .optional();

//   max_quantity_per_product: z.union([z.string(), z.number()]).superRefine((val, ctx) => {
//         const error = nonEmptyOverride(val as string, "Max Quantity per Product is required");
//         if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
//     }),
// ---------------------------------------------------------------------------
// Root bundle schema (used for both Base and Variants)
// ---------------------------------------------------------------------------
export const BundleSchema = z
    .object({
        // Metadata / identifiers (not controlled by the form)
        status: boolean(),
        bundle_type: optionalString(),
        is_paused: optionalBoolean(),
        form_id: optionalString(),

        // Behavior toggles
        hide_out_of_stock_products: optionalBoolean(),
        show_search_bar: optionalBoolean(),
        search_bar_alignment_desktop: optionalString("flex-end"),
        search_bar_alignment_mobile: optionalString("center"),
        auto_advance_when_condition_matched: optionalBoolean(),
        hide_product_info_modal: optionalBoolean(),
        disable_product_title_link: optionalBoolean(),
        hide_bundle_header: optionalBoolean(),
        allow_quantity_input: optionalBoolean(),
        secondary_button_type: optionalString("edit_bundle"),
        limit_max_quantity_per_product: optionalBoolean(),
        max_quantity_per_product: optionalNumber(5),
        footer_layout: optionalString(),
        selected_template_bb: optionalString(),

        bundle_as_product: BundleAsProductSchema,
        isProductPageVisible: requiredBoolean("Is Product Page Visible is required"),
        // Cross-field validated in the root superRefine below (needs tier_discount_basis)
        discount_options: arrayOf(BBTierOptionSchema),
        show_progress_bar: optionalBoolean(),
        disable_cta_when_stock_reached: requiredBoolean("", false),
        no_products_selected_text: optionalString(),
        file_upload_uploading_text: optionalString(),
        file_upload_click_to_upload_multiple_text: optionalString(),
        file_upload_click_to_upload_single_text: optionalString(),
        file_upload_max_size_text: optionalString(),
        file_upload_already_uploaded_error: optionalString(),
        file_upload_only_one_file_error: optionalString(),
        show_product_grid_view: boolean(false),
        show_broken_quantity_in_grid: boolean(false),

        // Design (per-variant, not a skip_key)
        bundles_design: BundlesDesignSchema,

        order_discounts: optionalBoolean(true),
        product_discounts: optionalBoolean(true),
        shipping_discounts: optionalBoolean(true),

        // Content
        // bundle_name: z.string(),
        title: requiredString("Title is required"),
        top_html: string(""),
        discount_type: requiredString("Type is required"),
        tier_discount_basis: requiredEnum(
            ["items_count", "bundle_total", "product_based"],
            "Tier discount basis is required",
        ),
        discount_value: requiredNumber("Discount value is required"),
        hide_prices_product_card: optionalBoolean(),
        steps: z.array(StepSchema).superRefine((steps, ctx) => {
            stepLoop: for (let i = 0; i < steps.length; i++) {
                const step = steps[i];
                if (!step?.step_label?.trim()) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Step ${i + 1}: Step title is required`,
                        path: [i, "step_label"],
                    });
                    continue stepLoop;
                }
                if (!["fields", "products", "free_gift"].includes(step?.step_content_type)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Step ${i + 1}: Invalid content type`,
                        path: [i, "step_content_type"],
                    });
                    continue stepLoop;
                }
                if (!["specific", "collection"].includes(step?.product_selection)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Step ${i + 1}: Invalid product selection`,
                        path: [i, "product_selection"],
                    });
                    continue stepLoop;
                }
                if (
                    (step?.step_content_type === "products" || step?.step_content_type === "free_gift") &&
                    !step?.enable_subSteps
                ) {
                    if (step?.product_selection === "specific" && (!step?.products || step.products.length === 0)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Step ${i + 1}: At least one product must be selected for 'Specific Products' selection`,
                            path: [i, "products"],
                        });
                        continue stepLoop;
                    }
                    if (
                        step?.product_selection === "collection" &&
                        (!step?.collections || step.collections.length === 0)
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Step ${i + 1}: At least one collection must be selected for 'Collection' selection`,
                            path: [i, "collections"],
                        });
                        continue stepLoop;
                    }
                }
                if (step?.enable_subSteps) {
                    if (!step?.subSteps || step.subSteps.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Step ${i + 1}: At least one Category must be added`,
                            path: [i, "subSteps"],
                        });
                        continue stepLoop;
                    }
                    for (let j = 0; j < step.subSteps.length; j++) {
                        const subStep = step.subSteps[j];
                        if (!subStep?.step_label?.trim()) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Category ${j + 1}: Category label is required`,
                                path: [i, "subSteps", j, "step_label"],
                            });
                            continue stepLoop;
                        }
                        if (!["specific", "collection"].includes(subStep?.product_selection)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Category ${j + 1}: Invalid product selection`,
                                path: [i, "subSteps", j, "product_selection"],
                            });
                            continue stepLoop;
                        }
                        if (
                            subStep?.product_selection === "specific" &&
                            (!subStep?.products || subStep.products.length === 0)
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Category ${j + 1}: At least one product must be selected for 'Specific Products' selection`,
                                path: [i, "subSteps", j, "products"],
                            });
                            continue stepLoop;
                        }
                        if (
                            subStep?.product_selection === "collection" &&
                            (!subStep?.collections || subStep.collections.length === 0)
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Category ${j + 1}: At least one collection must be selected for 'Collection' selection`,
                                path: [i, "subSteps", j, "collections"],
                            });
                            continue stepLoop;
                        }
                        if (
                            !["min_products", "max_products", "exact_products"].includes(
                                subStep?.subStep_condition_type,
                            )
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Category ${j + 1}: Invalid condition type`,
                                path: [i, "subSteps", j, "subStep_condition_type"],
                            });
                            continue stepLoop;
                        }
                        if (+subStep.product_count_limit <= 0) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Category ${j + 1}: ${
                                    subStep?.subStep_condition_basis === "bundle_total" ? "Amount" : "Items count"
                                } must be greater than zero`,
                                path: [i, "subSteps", j, "product_count_limit"],
                            });
                            continue stepLoop;
                        }
                    }
                }
                if (step?.step_conditions) {
                    if (step.step_conditions.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Step ${i + 1}: At least one Step Condition must be added`,
                            path: [i, "step_conditions"],
                        });
                        continue stepLoop;
                    }
                    const seenConditions = new Set<string>();
                    for (let j = 0; j < step.step_conditions.length; j++) {
                        const cond = step.step_conditions[j];
                        const key = `${cond.basis}_${cond.type}`;
                        if (+cond?.limit <= 0) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Condition ${j + 1}: ${
                                    cond.basis === "bundle_total" ? "Amount" : "Items count"
                                } must be greater than zero`,
                                path: [i, "step_conditions", j, "limit"],
                            });
                            continue stepLoop;
                        }
                        if (seenConditions.has(key)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1} -> Condition ${j + 1}: Duplicate condition found`,
                                path: [i, "step_conditions", j, "type"],
                            });
                            continue stepLoop;
                        }
                        seenConditions.add(key);
                    }
                }
                if (step?.step_content_type === "fields") {
                    if (!step?.fields || step.fields.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Step ${i + 1}: At least one field is required`,
                            path: [i, "fields"],
                        });
                        continue stepLoop;
                    }
                    for (let j = 0; j < step.fields.length; j++) {
                        const field = step.fields[j];
                        if (!["input", "textarea", "dropdown", "file_upload"].includes(field.field_type)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1}, Field ${j + 1}: Invalid field type`,
                                path: [i, "fields", j, "field_type"],
                            });
                            continue stepLoop;
                        }
                        if (!field.field_title?.trim()) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Step ${i + 1}, Field ${j + 1}: Field title is required`,
                                path: [i, "fields", j, "field_title"],
                            });
                            continue stepLoop;
                        }
                        if (field.field_type === "dropdown") {
                            if (!field.dropdown_options || field.dropdown_options.length === 0) {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: `Step ${i + 1}, Field ${j + 1}: At least one option is required for dropdown fields`,
                                    path: [i, "fields", j, "dropdown_options"],
                                });
                                continue stepLoop;
                            }
                        }
                        if (field.field_type === "file_upload") {
                            if (
                                !field.file_upload_settings?.allowed_file_types ||
                                field.file_upload_settings.allowed_file_types.length === 0
                            ) {
                                ctx.addIssue({
                                    code: z.ZodIssueCode.custom,
                                    message: `Step ${i + 1}, Field ${j + 1}: At least one file type must be selected`,
                                    path: [i, "fields", j, "file_upload_settings", "allowed_file_types"],
                                });
                                continue stepLoop;
                            }
                        }
                    }
                }
            }
        }),
        default_step: loose(),
        review_step: ReviewStepSchema.superRefine((step, ctx) => {
            if (!step?.is_enabled) return;
            if (!step.step_label || step.step_label.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Review Step Page Title is required",
                    path: ["step_label"],
                });
            }
            if (step.enable_addon_products) {
                if (!step.addon_section_title || step.addon_section_title.trim().length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Add-on section title is required",
                        path: ["addon_section_title"],
                    });
                }
                if (step.addon_product_selection === "specific") {
                    if (!step.addon_products || step.addon_products.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "At least one product must be selected for 'Specific Products' selection",
                            path: ["addon_products"],
                        });
                    }
                }
                if (step.addon_product_selection === "collection") {
                    if (!step.collections || step.collections.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "At least one collection must be selected for 'Collection' selection",
                            path: ["collections"],
                        });
                    }
                }
            }
        }),
        products: looseArray(),
        bundle_product_level: optionalEnum(["product", "variant"]),
        footer_total_text: requiredString("Footer total text value is required"),
        footer_text: requiredString("Footer text value is required"),
        free_text: requiredString("Free text value is required"),
        item_added_text: requiredString("Cart total text value is required"),
        buy_now_cta_text: requiredString("Buy Now CTA text value is required"),
        bundle_ready_text: requiredString("Bundle ready text value is required"),
        edit_cta_text: requiredString("Edit bundle CTA text value is required"),
        start_again_cta_text: requiredString("Start again CTA text value is required"),
        next_cta_text: requiredString("Next CTA text value is required"),
        previous_cta_text: requiredString("Previous CTA text value is required", "Go to Checkout"),
        checkout_cta_text: requiredString("Checkout CTA text value is required"),
        checkout_continue_shopping_cta_text: requiredString("Checkout continue shopping cta text value is required"),
        checkout_view_cart_cta_text: requiredString("Checkout view cart cta text value is required"),
        checkout_box_label: requiredString("Checkout box label is required"),
        checkout_discount_label: requiredString("Checkout discount label is required"),
        show_more_text: requiredString("Show more text value is required"),
        show_less_text: requiredString("Show less text value is required"),
        out_of_stock_text: requiredString("Out of stock text value is required"),
        order_summary_title: requiredString("Order summary title text value is required"),
        order_summary_subtotal: requiredString("Order summary subtotal text value is required"),
        order_summary_discount: requiredString("Order summary discount text value is required"),
        empty_card_footer_text: requiredString("Empty card footer text value is required"),

        // Dates (skip_key)
        start_date: optionalString(),
        end_date: nullableOptionalString(null),

        use_compare_at_price: optionalBoolean(false),
        show_product_price_with_compare_price: optionalBoolean(false),
        allow_free_gifts: optionalBoolean(),
        free_gifts: looseArray(),
        free_gift_text: requiredString("Free gift Title is required"),
        bundle_embed_id: optionalString(),
        show_swatches: optionalBoolean(),
        swatchOptions: looseArray(),
        swatchesProductsMap: looseObject(),
        swatchShape: optionalString(),
        swatchSize: optionalNumber(),
        offer_cta_text: requiredString("Upsell button text is required"),
        limit_show_offer_cta: optionalEnum(["all", "products", "collections", "none"]),
        show_offer_cta_pages: looseArray(),
        offer_cta_position: requiredString("Offer CTA text is required"),

        // Bundle visibility link (skip_key)
        bundle_builder_link: z
            .string()
            .optional()
            .superRefine((val, ctx) => {
                if (!val || typeof val !== "string") return;
                // Split by '/pages/' and take the last segment
                const parts = val.split("/pages/");
                const slug = parts.pop()?.trim() || "";
                if (!slug.length) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Page slug (after /pages/) is required." });
                }
            }),

        allow_pre_selected_products: optionalBoolean(),
        pre_selected_products: looseArray(),
        make_pre_selected_compulsory: optionalBoolean(),
        product_card_desktop_size: optionalString(),
        product_card_mobile_size: optionalString(),
        allow_conditional_logic: optionalBoolean(),
        product_conditions: looseArray(),
        redirect_options: requiredEnum(["checkout", "cart"], "Redirect option is required", "checkout"),
        show_popup_on_cart_redirect: optionalBoolean(),
        popup_text: optionalString(),
        popup_redirect_url: optionalString().superRefine((val, ctx) => {
            if (!val) return;
            if (!z.url({ protocol: /^https?$/ }).safeParse(val).success) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Popup Redirect Link is not valid." });
            }
        }),
        apply_discount_on_compare_price: optionalBoolean(false),

        // offer_name (skip_key)
        offer_name: optionalString(),

        b2b_discounts_enabled: optionalBoolean(false),
        b2b_discount_type: requiredString("B2B discount type is required"),
        b2b_discount_value: requiredNumber("B2B discount value is required"),
        fixed_boxes: z
            .array(FixedBoxSchema)
            .default([])
            .superRefine((boxes, ctx) => {
                const seenItemCounts = new Set<number>();
                for (const box of boxes) {
                    if (!box.boxName?.trim()) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Box name cannot be empty." });
                        return;
                    }
                    if (box.itemsCount <= 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Items in box must be greater than zero.",
                        });
                        return;
                    }
                    if (box.fixedPrice <= 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Fixed price must be greater than zero.",
                        });
                        return;
                    }
                    if (seenItemCounts.has(box.itemsCount)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Duplicate quantity found in boxes. Each box must have a unique number of items.",
                        });
                        return;
                    }
                    seenItemCounts.add(box.itemsCount);
                }
            }),
        fixed_boxes_upsell_message: requiredString("Upsell message is required", ""),
        fixed_boxes_success_message: requiredString("Success message is required", ""),
        substep_layout: optionalString("tabs"),
        show_total_quantity_in_footer: optionalBoolean(),
        choose_option_text: requiredString("Choose option text is required"),

        // Targeting (skip_key)
        targeting: TargetingSchema,

        // Settings (skip_keys)
        general_settings: GeneralSettingsSchema,
        bundles_settings: BundleSettingsSchema,

        // product_bundle_id: z.string().optional(),
        // max_width: z.number().optional(),
        // SwatchesBundleConfigTouchedRef: z.any().optional(),
        allow_exclude_from_step_conditions: optionalBoolean(),

        // A/B testing — always taken from Base/Variant A (skip_key)
        ab_config: loose(),
    })
    .superRefine((data, ctx) => {
        if (data.isProductPageVisible) {
            if (!data.bundle_as_product?.product_title) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["bundle_as_product", "product_title"],
                    message: "Bundle Product Title is required",
                });
            } else if (data.bundle_as_product.product_title.length < 3) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["bundle_as_product", "product_title"],
                    message: "Bundle Product Title must be at least 3 characters long",
                });
            }
        }

        if (data.limit_max_quantity_per_product) {
            const maxQty = data.max_quantity_per_product;
            if (typeof maxQty !== "number" || Number.isNaN(maxQty) || maxQty <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Max Quantity per Product is required",
                    path: ["max_quantity_per_product"],
                });
            }
        }

        if (
            (data.limit_show_offer_cta === "products" || data.limit_show_offer_cta === "collections") &&
            (!data.show_offer_cta_pages || (data.show_offer_cta_pages as any[]).length === 0)
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                    data.limit_show_offer_cta === "products"
                        ? "At least one product must be selected"
                        : "At least one collection must be selected",
                path: ["show_offer_cta_pages"],
            });
        }

        // discount_options <- needs tier_discount_basis
        const discountOptions = data.discount_options ?? [];
        if (discountOptions.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "No discount options provided.",
                path: ["discount_options"],
            });
        } else {
            for (let i = 0; i < discountOptions.length; i++) {
                const option = discountOptions[i];
                if (typeof option.discountValue !== "number" || Number.isNaN(option.discountValue)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Tier ${i + 1}: Discount value must be a number.`,
                        path: ["discount_options", i, "discountValue"],
                    });
                    continue;
                }
                if (option.discountValue < 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Tier ${i + 1}: Discount value must be greater than or equal to 0.`,
                        path: ["discount_options", i, "discountValue"],
                    });
                    continue;
                }
                if (option.discountType === "percentage" && option.discountValue > 100) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Tier ${i + 1}: max 100, discount value in option`,
                        path: ["discount_options", i, "discountValue"],
                    });
                    continue;
                }
                if (data.tier_discount_basis === "product_based") {
                    if (!option.selected_products || option.selected_products.length === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Group ${i + 1}: At least one product must be selected.`,
                            path: ["discount_options", i, "selected_products"],
                        });
                        continue;
                    }
                } else if (i > 0) {
                    const previousOpt = discountOptions[i - 1];
                    if (previousOpt && option.quantity <= previousOpt.quantity) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Tier ${i + 1}: Minimum quantity must be greater than the previous tier's quantity.`,
                            path: ["discount_options", i, "quantity"],
                        });
                        continue;
                    }
                }
            }
        }

        // free_gifts <- needs allow_free_gifts
        if (data.allow_free_gifts) {
            const freeGifts = (data.free_gifts as any[]) || [];

            if (freeGifts.length <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: "Free gift is required",
                    path: ["free_gifts"],
                });
            } else {
                freeGifts.forEach((p, idx) => {
                    if (p.quantity < 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Free gift quantity cannot be negative",
                            path: ["free_gifts", idx, "quantity"],
                        });
                    }
                    if (p.quantity === 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Free gift quantity cannot be zero",
                            path: ["free_gifts", idx, "quantity"],
                        });
                    }
                });
            }
        }
    });

export type BundleFormValues = z.infer<typeof BundleSchema>;
